import { useState, useCallback } from 'react';
import { apiConfig } from '../config';

export interface OrderTopping {
  id: number;
  topping_id: number;
  topping_name: string;
}

export interface Order {
  id: number;
  order_type: string;
  order_date: string;
  order_status: string;
  menu_item_id?: number;
  menu_item_name?: string;
  menu_item_description?: string;
  special_id?: number;
  special_name?: string;
  special_description?: string;
  toppings?: OrderTopping[];
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface CutoffStatus {
  is_open: boolean;
  target_date: string;
  message: string;
}

interface CreateOrderRequest {
  order_type: 'regular' | 'salad' | 'special';
  meal_period?: 'lunch' | 'midnight';
  order_date: string;
  menu_item_id?: number;
  special_id?: number;
  topping_ids?: number[];
  option_ids?: number[];
  notes?: string;
  ticket_code?: string;
}

export function useCreateOrder() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createOrder = useCallback(async (
    accessToken: string,
    orderData: CreateOrderRequest
  ): Promise<Order | null> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiConfig.baseUrl}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(orderData),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || 'Failed to create order');
        return null;
      }

      return data;
    } catch (err) {
      setError('Network error creating order');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createOrder, loading, error };
}

export function useMyOrders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async (accessToken: string, orderDate?: string) => {
    setLoading(true);
    setError(null);
    try {
      const url = orderDate
        ? `${apiConfig.baseUrl}/orders/mine?order_date=${orderDate}`
        : `${apiConfig.baseUrl}/orders/mine`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      if (!response.ok) throw new Error('Failed to fetch orders');

      const data = await response.json();
      setOrders(data);
      return data;
    } catch (err) {
      setError('Failed to load orders');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  return { orders, fetchOrders, loading, error, setOrders };
}

export function useCutoffStatus() {
  const [status, setStatus] = useState<CutoffStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkCutoff = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${apiConfig.baseUrl}/orders/cutoff`
      );
      if (response.ok) {
        const data = await response.json();
        // Map order_date to target_date for consistency
        setStatus({
          is_open: data.is_open,
          target_date: data.order_date || data.target_date,
          message: data.message,
        });
      } else {
        setError('Failed to check cutoff status');
      }
    } catch (err) {
      console.error('Failed to check cutoff:', err);
      setError('Network error checking cutoff');
    } finally {
      setLoading(false);
    }
  }, []);

  return { status, checkCutoff, loading, error };
}

export function useCancelOrder() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancelOrder = useCallback(async (accessToken: string, orderId: number): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiConfig.baseUrl}/orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.detail || 'Failed to cancel order');
        return false;
      }
      return true;
    } catch (err) {
      setError('Network error');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return { cancelOrder, loading, error };
}
